/**
 * ArtefactBuilderPanel — a chat-style composer for building a table artefact.
 *
 * The writer types a plain-language instruction and tags source tables inline
 * with ``@`` — the exact same mention experience as the chat rail (via the
 * shared ``MentionTextArea``: Section/Table → file → caption). While the
 * artefact builds, per-step SSE progress is shown live. On success the shell
 * opens the preview/approve modal.
 *
 * Selection/build/streaming live in the shell — this owns only the composer +
 * progress presentation.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useState } from "react";
import type { ArtefactStageEvent } from "../../../api/client";
import type { MentionAttachment } from "../../../types";
import MentionTextArea from "../../components/MentionTextArea";
import { brandColors, fonts, inkColors, surfaceColors } from "../../tokens";

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 10, 30, 0.55)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: fonts.body,
  },
  panel: {
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderTop: `3px solid ${brandColors.primary}`,
    borderRadius: "4px",
    width: "100%",
    maxWidth: "480px",
    maxHeight: "82vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 48px rgba(15, 10, 30, 0.25)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px 10px",
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  title: { fontFamily: fonts.display, fontSize: "15px", fontWeight: 700, color: inkColors.ink },
  closeBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    lineHeight: 1,
    color: inkColors.ink4,
    padding: "2px 6px",
  },
  body: { padding: "12px 16px", display: "flex", flexDirection: "column", rowGap: "8px", overflowY: "auto" },
  guidance: { fontSize: "11px", color: inkColors.ink3, lineHeight: 1.45 },
  guidanceAt: { fontFamily: fonts.mono, fontWeight: 700, color: brandColors.primary },
  count: { fontSize: "9.5px", fontFamily: fonts.mono, color: inkColors.ink4, minHeight: "12px" },
  error: { fontSize: "10.5px", color: "#b42318", lineHeight: 1.4 },
  progress: {
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "4px",
    background: surfaceColors.paperDim,
    padding: "8px 10px",
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    maxHeight: "160px",
    overflowY: "auto",
  },
  stageRow: { display: "flex", alignItems: "baseline", columnGap: "7px", fontSize: "10.5px", color: inkColors.ink2 },
  stageDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    background: brandColors.primary,
    flexShrink: 0,
    transform: "translateY(-1px)",
  },
  stageCurrent: { color: inkColors.ink, fontWeight: 600 },
  footer: { display: "flex", gap: "8px", padding: "12px 16px", borderTop: `1px solid ${surfaceColors.line}` },
  btn: {
    flex: 1,
    fontSize: "12px",
    fontWeight: 700,
    padding: "9px 14px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    border: "none",
  },
  btnPrimary: { background: brandColors.primary, color: "#fff" },
  btnSecondary: { background: "#fff", color: inkColors.ink2, border: `1px solid ${surfaceColors.lineStrong}` },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
});

export interface ArtefactBuilderPanelProps {
  libraryId: string | null;
  building?: boolean;
  buildError?: string | null;
  /** Live SSE progress messages, in arrival order. */
  stages?: ArtefactStageEvent[];
  onCancel: () => void;
  onBuild: (tables: MentionAttachment[], instruction: string) => void;
}

export const ArtefactBuilderPanel: React.FC<ArtefactBuilderPanelProps> = ({
  libraryId,
  building = false,
  buildError = null,
  stages = [],
  onCancel,
  onBuild,
}) => {
  const styles = useStyles();
  const [value, setValue] = useState("");
  const [attachments, setAttachments] = useState<MentionAttachment[]>([]);

  const tableCount = attachments.filter((a) => a.assetType === "table").length;
  const canBuild = tableCount >= 1 && value.trim().length > 0 && !building;

  return (
    <dialog open className={styles.backdrop} aria-modal="true" aria-labelledby="artefact-builder-title">
      <div className={styles.panel}>
        <div className={styles.header}>
          <div id="artefact-builder-title" className={styles.title}>
            Create artefact
          </div>
          <button type="button" className={styles.closeBtn} onClick={onCancel} aria-label="Close" disabled={building}>
            ×
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.guidance}>
            Describe the table you want. Type <span className={styles.guidanceAt}>@</span> to tag a specific source
            table, then say how to merge, filter, or add computed columns.
          </div>

          <MentionTextArea
            libraryId={libraryId}
            value={value}
            onChange={setValue}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            disabled={building}
            placeholder="e.g. Merge @… with @… on Subject ID, keep the treatment arm, and add a % change column from baseline to week 12."
            testId="artefact-composer"
          />

          <div className={styles.count}>
            {tableCount === 0 ? "Tag at least one table with @" : `${tableCount} table(s) tagged`}
          </div>

          {buildError && <div className={styles.error}>{buildError}</div>}

          {building && stages.length > 0 && (
            <div className={styles.progress} data-testid="artefact-progress" aria-live="polite">
              {stages.map((s, i) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: append-only progress log
                  key={i}
                  className={mergeClasses(styles.stageRow, i === stages.length - 1 && styles.stageCurrent)}
                >
                  <span className={styles.stageDot} />
                  <span>{s.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={mergeClasses(styles.btn, styles.btnSecondary)}
            onClick={onCancel}
            disabled={building}
          >
            Cancel
          </button>
          <button
            type="button"
            className={mergeClasses(styles.btn, styles.btnPrimary, !canBuild && styles.btnDisabled)}
            onClick={() => canBuild && onBuild(attachments, value.trim())}
            disabled={!canBuild}
            data-testid="artefact-build-preview"
          >
            {building ? "Building…" : "Build preview"}
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default ArtefactBuilderPanel;
